import { createElement as _createElement } from "@emotion/react";
import { jsx as _jsx } from "@emotion/react/jsx-runtime";
import { useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { CustomRedirect } from '@/components/functional/Link/CustomRedirect/CustomRedirect';
import { propMatch } from '@/helpers/custom-prop-types';
import { apiGetListProffitSearchSlidePluck } from '../../../../actions/ProffitSearchSlideActions';
import { wrapDispatch } from '../../../../helpers/action';
import { sleep } from '../../../../helpers/common';
import { useSlideSearchSlideViewParams } from '../../../../helpers/use/proffit_search/common/useSlideSearchSlideParams';
import { useSlideSearchSlideSlides } from '../../../../helpers/use/proffit_search/common/useSlideSearchSlideSlides';
import { useSlideSearchCommonSlideType } from '../../../../helpers/use/proffit_search/common/useSlideSearchSlideType';
import { AllLoaderComponent } from '../../../Ui/Loading';
/**
 * スライド詳細表示用のデータを取得するためのHoc
 * @param ChildSkeletonComponent
 * @returns {function(*): (function(*): *)|*}
 * @constructor
 */
const SlideSearchOneHoc = (ChildSkeletonComponent) => (ChildComponent) => {
    const SlideSearchOneHocInner = (props) => {
        const { match: { params: { id }, }, } = props;
        const { search } = useLocation();
        const dispatch = useDispatch();
        const loadingRef = useRef(false);
        const rawSlides = useSelector((state) => state.proffitSearchSlide);
        const { ids, viewParams } = useSlideSearchSlideViewParams({ id, search });
        // URLの変更に伴って下部コンポーネントのstateを初期化するためのkey
        const refreshKey = useMemo(() => JSON.stringify({ id, params: viewParams }), [id, viewParams]);
        const { categories, images } = useSlideSearchCommonSlideType.useSlideSearchSlideTypeValues();
        const { defaultImageIndexMap, defaultSlideIndex, mainSlide, notLoadedListIds, slides, } = useSlideSearchSlideSlides({
            id,
            ids,
            rawSlides,
            viewParams,
        });
        useEffect(() => {
            // 再取得対象のスライドがある場合は再読み込み
            if (notLoadedListIds.length > 0 && !loadingRef.current) {
                loadingRef.current = true;
                const action = apiGetListProffitSearchSlidePluck({
                    ids: notLoadedListIds,
                });
                sleep(0.7).then(() => {
                    wrapDispatch(dispatch, action).finally(() => {
                        loadingRef.current = false;
                    });
                });
            }
        }, [categories, dispatch, images, notLoadedListIds, viewParams, loadingRef]);
        // 読み込み前はスケルトン表示
        if (notLoadedListIds.length > 0) {
            if (ChildSkeletonComponent) {
                return _jsx(ChildSkeletonComponent, {});
            }
            return _jsx(AllLoaderComponent, {});
        }
        // メインスライドが存在しない場合はリダイレクト
        if (!mainSlide) {
            return _jsx(CustomRedirect, { to: '/proffit_search/search' });
        }
        return (_createElement(ChildComponent, Object.assign({}, props, { defaultImageIndexMap: defaultImageIndexMap, defaultSlideIndex: defaultSlideIndex, key: refreshKey, slide: mainSlide, slides: slides })));
    };
    SlideSearchOneHocInner.propTypes = {
        match: propMatch(),
    };
    return SlideSearchOneHocInner;
};
export default SlideSearchOneHoc;
